import {useEffect,useRef,useState} from 'react';
import {loadStripeTestCheckouts,type StripeTestCheckoutPage,type StripeTestCheckoutReadback,type StripeTestQuoteReadback} from './api';
import {approveStripeTestQuote,attemptMatches,createStripeTestCheckout,prepareAttempt,retainedAttempt,resumeStripeTestCheckout,
  type CheckoutAttempt,type CheckoutResult,type CheckoutScope} from './stripeTestCheckoutActions';

const money=(minor:number)=>new Intl.NumberFormat(undefined,{style:'currency',currency:'USD'}).format(minor/100);
const hold='TEST checkout is held. Reload local records or request authorized reconciliation; do not start a replacement payment.';

export function CustomerStripeTestCheckoutActions({enabled,identityId,organizationId}:{enabled:boolean}&CheckoutScope) {
  if(enabled!==true)return null;
  // Replacing the scope unmounts all old state, including any hosted link.
  return <ScopedActions key={`${organizationId}:${identityId}`} scope={{identityId,organizationId}}/>;
}

function ScopedActions({scope}:{scope:CheckoutScope}) {
  const [page,setPage]=useState<StripeTestCheckoutPage|null>(null);
  const [cursor,setCursor]=useState<string|undefined>();
  const [reload,setReload]=useState(0);
  const [pending,setPending]=useState(true);
  const [message,setMessage]=useState('');
  const [link,setLink]=useState<string|null>(null);
  const busy=useRef(true);
  const generation=useRef(0);
  const mutation=useRef<AbortController|null>(null);

  useEffect(()=>{
    const current=++generation.current;
    const controller=new AbortController();
    setPage(null);setLink(null);setMessage('');setPending(true);busy.current=true;
    loadStripeTestCheckouts(cursor,controller.signal).then(value=>{
      if(generation.current===current)setPage(value);
    }).catch(()=>{if(generation.current===current)setMessage(hold);})
      .finally(()=>{if(generation.current===current){setPending(false);busy.current=false;}});
    return ()=>{generation.current++;controller.abort();mutation.current?.abort();};
  },[cursor,reload]);

  function refresh(next?:string){if(busy.current)return;setCursor(next);setReload(value=>value+1);}

  async function run(action:(signal:AbortSignal)=>Promise<void>) {
    if(busy.current)return;
    busy.current=true;setPending(true);setLink(null);setMessage('');
    const controller=new AbortController();mutation.current=controller;
    const current=generation.current;
    try {await action(controller.signal);}
    catch {if(generation.current===current){setPage(null);setMessage(hold);}}
    finally {if(generation.current===current){busy.current=false;setPending(false);mutation.current=null;}}
  }

  function acceptResult(result:CheckoutResult,signal:AbortSignal) {
    if(signal.aborted)return;
    // This is only a creation/resume result. Invoice settlement remains the separate local readback.
    setPage(value=>value?{...value,checkouts:value.checkouts.map(order=>order.order_id===result.order_id
      ?{...order,invoices:[{...result,refunded_minor:0,reconciliation_required:result.state==='paid_held'}]}:order)}:null);
    setLink(result.checkout_url??null);
    setMessage(result.checkout_url?'Existing TEST session is available. Open Stripe only when you choose below.':hold);
  }

  function approve(quote:StripeTestQuoteReadback){void run(async signal=>{
    const request=prepareAttempt(scope,quote,'approve');
    const approved=await approveStripeTestQuote(request,quote,signal);
    if(signal.aborted)return;
    setPage(value=>value?{...value,checkouts:value.checkouts.map(order=>order.order_id===quote.order_id?{...order,quotes:[approved]}:order)}:null);
    setMessage('This exact TEST quote is approved. Opening a TEST checkout requires a separate action.');
  });}
  function create(quote:StripeTestQuoteReadback,recovery:CheckoutAttempt|null,expectedIntentId?:string){void run(async signal=>{
    // Recovery never allocates a key. A missing/changed journal is a hold, not permission to create.
    const request=recovery??prepareAttempt(scope,quote,'create');
    acceptResult(await createStripeTestCheckout(request,quote,recovery!==null,signal,expectedIntentId),signal);
  });}
  function resume(order:StripeTestCheckoutReadback){void run(async signal=>{
    const invoice=order.invoices[0];
    acceptResult(await resumeStripeTestCheckout(order.order_id,invoice.intent_id,invoice.amount_minor,signal),signal);
  });}

  function controls(order:StripeTestCheckoutReadback) {
    let approval:CheckoutAttempt|null,creation:CheckoutAttempt|null;
    try {approval=retainedAttempt(scope,order.order_id,'approve');creation=retainedAttempt(scope,order.order_id,'create');}
    catch {return <p>{hold}</p>;}
    const quote=order.quotes.length===1?order.quotes[0]:null;
    if(order.order_state!=='ready_for_review'||!quote||quote.order_version!==order.order_version
      ||order.quote_history_truncated||order.intent_history_truncated||order.invoices.length>1)return <p>{hold}</p>;
    const invoice=order.invoices[0];
    if(invoice&&(invoice.reconciliation_required||invoice.refunded_minor!==0||invoice.amount_minor!==quote.amount_minor
      ||!['open','creation_unknown'].includes(invoice.state)))return <p>{hold}</p>;
    if(quote.state==='confirmed'){
      if(invoice||creation||(approval&&!attemptMatches(approval,quote)))return <p>{hold}</p>;
      return <button type="button" disabled={pending} onClick={()=>approve(quote)}>Approve this {money(quote.amount_minor)} USD TEST quote</button>;
    }
    if(creation&&!attemptMatches(creation,quote))return <p>{hold}</p>;
    if(invoice?.state==='open')return <button type="button" disabled={pending} onClick={()=>resume(order)}>Resume existing TEST checkout</button>;
    if(creation)return <><p>A saved checkout request exists. Recovery uses its original key and exact versions; it may remain held.</p>
      <button type="button" disabled={pending} onClick={()=>create(quote,creation,invoice?.intent_id)}>Recover the same TEST checkout request</button></>;
    if(invoice)return <p>{hold} The original request is not available in this browser session.</p>;
    return <button type="button" disabled={pending} onClick={()=>create(quote,null)}>Create TEST checkout for {money(quote.amount_minor)} USD</button>;
  }

  return <section className="panel" aria-labelledby="stripe-test-actions-title">
    <p className="eyebrow">PROFENZ, INC. · STRIPE TEST</p>
    <h2 id="stripe-test-actions-title">Review and open a TEST checkout</h2>
    <p>No live charge is available. Review the exact amount, order and quote versions before approving. Card details belong only on Stripe’s hosted page.</p>
    <p>Only explicit actions below send a checkout request. Keep this browser session for uncertain-request recovery. A return or cancel URL never confirms payment; reload the separate TEST invoice for the local reconciled state.</p>
    <p role="status" aria-live="polite">{message||(pending?'Loading TEST quotes…':'')}</p>
    {link&&<p><a href={link} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">Open the existing Stripe TEST checkout</a></p>}
    <button type="button" disabled={pending} onClick={()=>refresh()}>Reload TEST checkout actions</button>
    {page?.checkouts.length===0&&<p>No TEST quotes are available.</p>}
    {page&&<ol className="intake-list">{page.checkouts.map(order=><li className="intake-card" key={order.order_id}>
      <h3>TEST checkout for order {order.order_id}</h3>
      <p>Order version {order.order_version} · {order.order_state}</p>
      {order.quotes.map(quote=><p key={quote.quote_id}>Quote {quote.quote_id} · version {quote.quote_version} · details version {quote.details_version} · {money(quote.amount_minor)} USD · {quote.state}</p>)}
      {controls(order)}
    </li>)}</ol>}
    {page?.next_order_id&&<button type="button" disabled={pending} onClick={()=>refresh(page.next_order_id??undefined)}>Next TEST quote page</button>}
  </section>;
}
